import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search,
  Calendar,
  Clock,
  Play,
  Pause,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { schedulesService, gardensService, controllersService, companiesService } from '@/services';
import type { ApiSchedule, CreateScheduleRequest, UpdateScheduleRequest } from '@/types/api';
import { cn } from '@/lib/utils';
import { Schedule } from '@/types';
import ScheduleFormDialog from '@/components/schedules/ScheduleFormDialog';
import ScheduleDeleteDialog from '@/components/schedules/ScheduleDeleteDialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function ScheduleCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-20" />
          <div className="h-12 w-px bg-border" />
          <div className="flex-1">
            <Skeleton className="h-5 w-48 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="w-8 h-8 rounded-full" />)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper para parsear startTime del API (formato "0000-01-01T06:30:00Z" -> "06:30")
function parseStartTime(startTime: string): string {
  // Si ya está en formato HH:mm, devolverlo
  if (/^\d{2}:\d{2}$/.test(startTime)) {
    return startTime;
  }
  // Si es ISO, extraer la hora
  try {
    const date = new Date(startTime);
    if (!isNaN(date.getTime())) {
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  } catch {
    // Fallback
  }
  // Intentar extraer manualmente de formato T##:##
  const match = startTime.match(/T(\d{2}:\d{2})/);
  if (match) {
    return match[1];
  }
  return '06:00'; // Default
}

// Helper to convert API schedule to frontend Schedule type
function apiScheduleToFrontend(apiSchedule: ApiSchedule): Schedule {
  return {
    id: apiSchedule.id,
    companyId: apiSchedule.companyId || undefined, // Convert null to undefined for frontend
    name: apiSchedule.name,
    gardenId: apiSchedule.gardenId || undefined, // Convert null to undefined for frontend
    assignments: apiSchedule.assignments?.map(a => ({
      id: a.id,
      scheduleId: apiSchedule.id,
      controllerId: a.controllerId,
      valveId: a.valveId,
    })) || [],
    startTime: parseStartTime(apiSchedule.startTime),
    duration: apiSchedule.duration,
    daysOfWeek: apiSchedule.daysOfWeek,
    enabled: apiSchedule.enabled,
    createdAt: new Date(apiSchedule.createdAt),
    updatedAt: new Date(apiSchedule.updatedAt),
  };
}

export default function Schedules() {
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentUser, isSuperAdmin } = useAuth();

  // Query para obtener programaciones
  // company_admin: solo ve programaciones de su empresa (filtrado automático por companyId o via gardenId)
  // superadmin: ve todas las programaciones, puede filtrar por companyId
  const { 
    data: schedulesData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['schedules', currentUser?.companyId],
    queryFn: async () => {
      // Para company_admin, el backend filtra automáticamente por companyId
      // Para superadmin, no enviamos companyId para ver todos
      const params: { pageSize: number; companyId?: string } = { pageSize: 100 };
      if (!isSuperAdmin && currentUser?.companyId) {
        // El backend filtra automáticamente, pero podemos enviarlo explícitamente si es necesario
        // Según la documentación, company_admin solo ve los de su empresa
      }
      const response = await schedulesService.getAll(params);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar programaciones');
      }
      return response.data;
    },
    enabled: !!currentUser, // Solo ejecutar cuando hay usuario autenticado
  });

  // Query para obtener jardines
  const { data: gardensData } = useQuery({
    queryKey: ['gardens'],
    queryFn: async () => {
      const response = await gardensService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar jardines');
      }
      return response.data;
    },
  });

  // Query para obtener controladoras
  const { data: controllersData } = useQuery({
    queryKey: ['controllers'],
    queryFn: async () => {
      const response = await controllersService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar controladoras');
      }
      return response.data;
    },
  });

  // Query para obtener empresas (solo para superadmin)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
    enabled: isSuperAdmin, // Solo cargar si es superadmin
  });

  // Mutation para crear
  const createMutation = useMutation({
    mutationFn: (data: CreateScheduleRequest) => schedulesService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        toast({ title: 'Programación creada', description: 'La programación ha sido creada correctamente.' });
        setFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para actualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduleRequest }) => 
      schedulesService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        toast({ title: 'Programación actualizada', description: 'La programación ha sido actualizada correctamente.' });
        setFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => schedulesService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        toast({ title: 'Programación eliminada', description: 'La programación ha sido eliminada.', variant: 'destructive' });
        setDeleteOpen(false);
        setSelectedSchedule(null);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para toggle enabled (usa endpoint /toggle)
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled?: boolean }) => 
      schedulesService.toggle(id, enabled),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        const newEnabled = response.data?.enabled ?? false;
        toast({ 
          title: newEnabled ? 'Programación activada' : 'Programación pausada',
          description: `La programación ha sido ${newEnabled ? 'activada' : 'pausada'}.`
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const apiSchedules = schedulesData?.items || [];
  const schedules = apiSchedules.map(apiScheduleToFrontend);
  const gardens = gardensData?.items || [];
  const controllers = controllersData?.items || [];

  const filteredSchedules = schedules.filter(schedule =>
    schedule.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getGardenName = (gardenId?: string) => {
    if (!gardenId) return '-';
    return gardens.find(g => g.id === gardenId)?.name || 'Sin asignar';
  };

  const getAssignmentsSummary = (schedule: Schedule) => {
    if (!schedule.assignments || schedule.assignments.length === 0) {
      return 'Sin asignar';
    }
    if (schedule.assignments.length === 1) {
      const a = schedule.assignments[0];
      const controller = controllers.find(c => c.id === a.controllerId);
      const valve = controller?.valves?.find(v => v.id === a.valveId);
      return `${controller?.name || 'Desconocido'} - ${valve?.name || 'Desconocida'}`;
    }
    return `${schedule.assignments.length} válvulas asignadas`;
  };

  const handleNewSchedule = () => {
    setSelectedSchedule(null);
    setFormOpen(true);
  };

  const handleEdit = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setFormOpen(true);
  };

  const handleDuplicate = async (schedule: Schedule) => {
    const duplicateData: CreateScheduleRequest = {
      name: `${schedule.name} (copia)`,
      gardenId: schedule.gardenId || '',
      startTime: schedule.startTime,
      duration: schedule.duration,
      daysOfWeek: schedule.daysOfWeek,
      enabled: false,
      assignments: schedule.assignments.map(a => ({
        controllerId: a.controllerId,
        valveId: a.valveId,
      })),
    };
    createMutation.mutate(duplicateData);
  };

  const handleDelete = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedSchedule) {
      deleteMutation.mutate(selectedSchedule.id);
    }
  };

  const handleToggleEnabled = (scheduleId: string, currentEnabled: boolean) => {
    // Si no se envía enabled, el backend hace auto-toggle
    toggleMutation.mutate({ id: scheduleId, enabled: !currentEnabled });
  };

  const handleSave = (data: Partial<Schedule>) => {
    // Validar que tenemos los campos requeridos
    if (!data.name || !data.startTime || !data.daysOfWeek || data.daysOfWeek.length === 0) {
      toast({ 
        title: 'Error de validación', 
        description: 'Por favor completa todos los campos requeridos.', 
        variant: 'destructive' 
      });
      return;
    }

    if (selectedSchedule) {
      // Actualizar programación existente
      const updateData: UpdateScheduleRequest = {
        name: data.name,
        gardenId: data.gardenId || null,
        startTime: data.startTime,
        duration: data.duration || 30,
        daysOfWeek: data.daysOfWeek,
        enabled: data.enabled ?? true,
        // Solo superadmin puede actualizar companyId
        companyId: isSuperAdmin ? (data.companyId || null) : undefined,
        assignments: data.assignments?.map(a => ({
          controllerId: a.controllerId,
          valveId: a.valveId,
        })) || [],
      };
      updateMutation.mutate({ id: selectedSchedule.id, data: updateData });
    } else {
      // Crear nueva programación
      const createData: CreateScheduleRequest = {
        name: data.name,
        gardenId: data.gardenId || null,
        startTime: data.startTime,
        duration: data.duration || 30,
        daysOfWeek: data.daysOfWeek,
        enabled: data.enabled ?? true,
        assignments: data.assignments?.map(a => ({
          controllerId: a.controllerId,
          valveId: a.valveId,
        })) || [],
        // company_admin: siempre incluye su companyId (el backend lo asigna automáticamente si no se envía)
        // superadmin: puede crear programaciones para cualquier empresa o sin empresa
        companyId: isSuperAdmin 
          ? (data.companyId || null) // Usar el companyId seleccionado en el formulario, o null para global
          : currentUser?.companyId || undefined,
      };
      createMutation.mutate(createData);
    }
  };

  // Stats
  const activeCount = schedules.filter(s => s.enabled).length;
  const pausedCount = schedules.filter(s => !s.enabled).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Programaciones</h1>
            <p className="text-muted-foreground mt-1">Configura los horarios de riego automático</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => <ScheduleCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Programaciones</h1>
            <p className="text-muted-foreground mt-1">Configura los horarios de riego automático</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar programaciones'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Programaciones
          </h1>
          <p className="text-muted-foreground mt-1">
            Configura los horarios de riego automático
          </p>
        </div>
        <Button
          onClick={handleNewSchedule}
          className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Programación
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar programaciones..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Play className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{activeCount}</p>
              <p className="text-sm text-muted-foreground">Activas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Pause className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{pausedCount}</p>
              <p className="text-sm text-muted-foreground">Pausadas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{schedules.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Schedules list */}
      <div className="space-y-3">
        {filteredSchedules.map((schedule) => (
          <motion.div key={schedule.id} variants={itemVariants}>
            <Card className="hover:shadow-card-hover transition-all duration-300">
              <CardContent className="p-4">
                {/* Mobile & Desktop Layout */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                  {/* Row 1: Time + Name + Status + Actions */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Time block */}
                    <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
                      <Clock className="w-4 h-4 text-primary" />
                      <div className="text-center">
                        <div className="text-lg font-display font-bold text-foreground leading-tight">
                          {schedule.startTime}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {schedule.duration} min
                        </div>
                      </div>
                    </div>

                    {/* Name and details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground truncate max-w-[200px] lg:max-w-none">
                          {schedule.name}
                        </h3>
                        <Badge 
                          variant={schedule.enabled ? 'default' : 'secondary'}
                          className="flex-shrink-0"
                        >
                          {schedule.enabled ? 'Activa' : 'Pausada'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        <span className="truncate max-w-[120px]">{getGardenName(schedule.gardenId)}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="truncate max-w-[150px] hidden sm:inline">{getAssignmentsSummary(schedule)}</span>
                      </div>
                    </div>

                    {/* Actions (visible always) */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch
                        checked={schedule.enabled}
                        onCheckedChange={() => handleToggleEnabled(schedule.id, schedule.enabled)}
                        disabled={toggleMutation.isPending}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(schedule)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(schedule)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(schedule)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Row 2 (mobile) / Same row (desktop): Days of week */}
                  <div className="flex items-center justify-between lg:justify-end gap-2">
                    {/* Assignment summary on mobile */}
                    <span className="text-xs text-muted-foreground truncate sm:hidden">
                      {getAssignmentsSummary(schedule)}
                    </span>
                    
                    {/* Days */}
                    <div className="flex gap-1 flex-shrink-0">
                      {daysOfWeek.map((day, index) => (
                        <div
                          key={day}
                          className={cn(
                            'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium transition-colors',
                            schedule.daysOfWeek.includes(index)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted/50 text-muted-foreground'
                          )}
                          title={day}
                        >
                          {day[0]}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredSchedules.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No hay programaciones configuradas.
          </p>
          <Button className="mt-4" variant="outline" onClick={handleNewSchedule}>
            <Plus className="w-4 h-4 mr-2" />
            Crear primera programación
          </Button>
        </motion.div>
      )}

      {/* Dialogs */}
      <ScheduleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        schedule={selectedSchedule}
        onSave={handleSave}
        isLoading={createMutation.isPending || updateMutation.isPending}
        gardens={gardensData?.items || []}
        controllers={controllersData?.items || []}
        companies={companiesData?.items || []}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
      />

      <ScheduleDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        schedule={selectedSchedule}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </motion.div>
  );
}
